import {
    type ReactElement,
    isValidElement,
    Children
} from "react";
import type {
    default as IInputGroupAddonProps
} from "./components/addon/type";
import {
    type InputGroupPartsConstantType,
    type InputGroupParts
} from "./type";
import InputGroupAddon from "./components/addon";
import InputGroupButton from "./components/button";
import InputGroupTextArea from "./components/textArea";

const getAddonElement = (child: unknown): ReactElement<IInputGroupAddonProps> | null => {
    if(!isValidElement<IInputGroupAddonProps>(child) || child.type !== InputGroupAddon) {
        return null;
    }

    return child;
};

const getHasButton = (addon: ReactElement<IInputGroupAddonProps>): boolean => {
    return Children.toArray(addon.props.children).some((child) => isValidElement(child) && child.type === InputGroupButton);
};

export const getInputGroupParts = ({
    children
}: InputGroupPartsConstantType): InputGroupParts => {
    const items = Children.toArray(children);

    const isMultiline = items.some((item) => isValidElement(item) && item.type === InputGroupTextArea);

    const parts: InputGroupParts = {
        trailingAttached: [],
        isActionable: false,
        leadingAttached: [],
        blockStart: [],
        blockEnd: [],
        rowItems: [],
        textArea: [],
        isMultiline
    };

    const startItems: Array<ReactElement> = [];

    const endItems: Array<ReactElement> = [];

    const otherItems: Array<ReactElement> = [];

    items.forEach((item) => {
        const addon = getAddonElement(item);

        if(!addon) {
            if(isValidElement(item) && item.type === InputGroupTextArea) {
                parts.textArea.push(item);

                return;
            }

            if(isValidElement(item)) {
                otherItems.push(item);
            }

            return;
        }

        const {
            variant = "inline",
            align = "start"
        } = addon.props;

        if(isMultiline) {
            const isStartSide = align === "start" || align === "blockStart";

            (isStartSide ? parts.blockStart : parts.blockEnd).push(addon);

            return;
        }

        if(getHasButton(addon)) {
            parts.isActionable = true;
        }

        const isStartSide = align === "start" || align === "blockStart";

        if(variant === "attached") {
            (isStartSide ? parts.leadingAttached : parts.trailingAttached).push(addon);

            return;
        }

        (isStartSide ? startItems : endItems).push(addon);
    });

    parts.rowItems = [
        ...startItems,
        ...otherItems,
        ...endItems
    ];

    return parts;
};
